import React, { useState, useEffect } from 'react';
import {
  Home,
  ShoppingCart,
  BarChart2,
  Settings,
  Package,
  LogOut,
  Menu as MenuIcon,
  X,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/logo1-removebg-preview.png';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const getMenuItems = (t: any, language: string) => {
  if (language === 'fr') {
    return [
      { name: t.dashboard, icon: Home, path: '/dashboard' },
      { name: t.sales, icon: ShoppingCart, path: '/dashboard/ventes' },
      { name: t.allSales, icon: BarChart2, path: '/dashboard/toutes-les-ventes' },
      { name: t.products, icon: Package, path: '/dashboard/produits' },
      { name: t.reports, icon: BarChart2, path: '/dashboard/rapports' },
      { name: t.settings, icon: Settings, path: '/dashboard/parametres' },
    ];
  } else {
    return [
      { name: t.dashboard, icon: Home, path: '/dashboard' },
      { name: t.sales, icon: ShoppingCart, path: '/dashboard/sales' },
      { name: t.allSales, icon: BarChart2, path: '/dashboard/all-sales' },
      { name: t.products, icon: Package, path: '/dashboard/products' },
      { name: t.reports, icon: BarChart2, path: '/dashboard/reports' },
      { name: t.settings, icon: Settings, path: '/dashboard/settings' },
    ];
  }
};

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  // Fermer le menu sur mobile lors du changement de route
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Gérer le scroll quand le menu est ouvert sur mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Traductions
  const text = {
    fr: {
      dashboard: 'Tableau de bord',
      sales: 'Nouvelle Vente',
      allSales: 'Toutes les Ventes',
      products: 'Produits',
      reports: 'Rapports',
      settings: 'Paramètres',
      logout: 'Déconnexion',
      menu: 'Menu',
    },
    en: {
      dashboard: 'Dashboard',
      sales: 'New Sale',
      allSales: 'All Sales',
      products: 'Products',
      reports: 'Reports',
      settings: 'Settings',
      logout: 'Logout',
      menu: 'Menu',
    }
  };

  const currentText = text[language as keyof typeof text];
  const menuItems = getMenuItems(currentText, language);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path: string) => {
    const currentPath = location.pathname;
    if (path === '/dashboard') {
      return currentPath === `/${language}/dashboard` || currentPath === `/${language}/dashboard/`;
    }
    return currentPath.startsWith(`/${language}${path}`);
  };

  return (
    <>
      {/* Bouton Menu Mobile */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-lg md:hidden hover:bg-gray-100 transition-colors"
        aria-label={currentText.menu}
      >
        {isOpen ? (
          <X className="h-6 w-6 text-gray-600" />
        ) : (
          <MenuIcon className="h-6 w-6 text-gray-600" />
        )}
      </button>

      {/* Overlay sur mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-screen bg-white border-r border-gray-200 shadow-lg z-40 transition-transform duration-300 transform
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
          md:translate-x-0 md:w-64 w-[280px]`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="mb-8 px-4 pt-6 border-b border-gray-100 pb-6 flex justify-center items-center">
            <img 
              src={logo} 
              alt="Genius Vente Logo" 
              className="h-12 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/')}
            />
          </div>

          {/* Menu items */}
          <nav className="flex-1 px-2 overflow-y-auto">
            <ul className="space-y-1">
              {menuItems.map((item) => (
                <li key={item.name}>
                  <button
                    onClick={() => navigate(`/${language}${item.path}`)}
                    className={`flex items-center w-full px-4 py-3 rounded-lg transition-all duration-150 group
                      ${isActive(item.path)
                        ? 'bg-[#0D3C4C] text-white'
                        : 'text-gray-600 hover:text-[#0D3C4C] hover:bg-[#0D3C4C]/5'
                      }`}
                  >
                    <item.icon 
                      className={`h-5 w-5 mr-3 transition-colors
                        ${isActive(item.path)
                          ? 'text-white'
                          : 'group-hover:text-[#0D3C4C]'
                        }`}
                    />
                    <span className="font-medium">{item.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Déconnexion en bas */}
          <div className="mt-auto p-4 border-t border-gray-100">
            <button 
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-gray-600 hover:text-[#0D3C4C] hover:bg-[#0D3C4C]/5 rounded-lg transition-all duration-150 group"
            >
              <LogOut className="h-5 w-5 mr-3 group-hover:text-[#0D3C4C] transition-colors" />
              <span className="font-medium">{currentText.logout}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
